/**
 * Call Outcome Modal - Appears after call ends to record outcome
 */

import { useState } from 'react';
import { CalendarIcon, CheckCircle2, PhoneOff, Phone, X, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

type Outcome = 'meeting_booked' | 'callback' | 'not_interested' | 'no_answer' | 'voicemail';

interface CallOutcomeModalProps {
  open: boolean;
  onClose: () => void;
  callId: string;
  prospectName?: string;
  onSubmit: (outcome: Outcome, notes?: string, callbackDate?: Date) => Promise<void>;
}

export function CallOutcomeModal({
  open,
  onClose,
  callId,
  prospectName,
  onSubmit,
}: CallOutcomeModalProps) {
  const [selectedOutcome, setSelectedOutcome] = useState<Outcome | null>(null);
  const [notes, setNotes] = useState('');
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const outcomeButtons: { value: Outcome; label: string; icon: React.ReactNode; color: string }[] = [
    {
      value: 'meeting_booked',
      label: 'Meeting Booked',
      icon: <CheckCircle2 className="h-5 w-5" />,
      color: 'bg-green-500 hover:bg-green-600 text-white',
    },
    {
      value: 'callback',
      label: 'Callback',
      icon: <CalendarIcon className="h-5 w-5" />,
      color: 'bg-blue-500 hover:bg-blue-600 text-white',
    },
    {
      value: 'not_interested',
      label: 'Not Interested',
      icon: <X className="h-5 w-5" />,
      color: 'bg-red-500 hover:bg-red-600 text-white',
    },
    {
      value: 'no_answer',
      label: 'No Answer',
      icon: <PhoneOff className="h-5 w-5" />,
      color: 'bg-gray-500 hover:bg-gray-600 text-white',
    },
    {
      value: 'voicemail',
      label: 'Voicemail',
      icon: <MessageSquare className="h-5 w-5" />,
      color: 'bg-orange-500 hover:bg-orange-600 text-white',
    },
  ];

  const handleOutcomeClick = (outcome: Outcome) => {
    setSelectedOutcome(outcome);
  };

  const handleSubmit = async () => {
    if (!selectedOutcome) return;

    setIsSubmitting(true);
    try {
      // Parse callback date/time if outcome is callback and date is provided
      let callbackDateTime: Date | undefined;
      if (selectedOutcome === 'callback' && callbackDate) {
        // Combine date and time, default time to 10:00 if not provided
        const timeStr = callbackTime || '10:00';
        callbackDateTime = new Date(`${callbackDate}T${timeStr}`);
      }

      await onSubmit(selectedOutcome, notes || undefined, callbackDateTime);

      // Reset and close
      setSelectedOutcome(null);
      setNotes('');
      setCallbackDate('');
      setCallbackTime('');
      onClose();
    } catch (error) {
      console.error('Failed to submit outcome:', error);
      // TODO: Show error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    setSelectedOutcome(null);
    setNotes('');
    setCallbackDate('');
    setCallbackTime('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>How did the call go?</DialogTitle>
          <DialogDescription>
            {prospectName ? `Call with ${prospectName}` : 'Record call outcome'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Outcome Buttons */}
          <div className="grid grid-cols-2 gap-3">
            {outcomeButtons.map((option) => (
              <Button
                key={option.value}
                variant={selectedOutcome === option.value ? 'default' : 'outline'}
                className={`h-16 justify-start gap-3 ${
                  selectedOutcome === option.value ? option.color : ''
                }`}
                onClick={() => handleOutcomeClick(option.value)}
              >
                {option.icon}
                <span className="font-semibold">{option.label}</span>
              </Button>
            ))}
          </div>

          {/* Callback Date/Time Picker (only show if callback selected) */}
          {selectedOutcome === 'callback' && (
            <div className="space-y-3 border-t pt-4">
              <Label>When should we call back?</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="callback-date" className="text-sm text-muted-foreground">
                    Date
                  </Label>
                  <Input
                    id="callback-date"
                    type="date"
                    value={callbackDate}
                    onChange={(e) => setCallbackDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label htmlFor="callback-time" className="text-sm text-muted-foreground">
                    Time (optional)
                  </Label>
                  <Input
                    id="callback-time"
                    type="time"
                    value={callbackTime}
                    onChange={(e) => setCallbackTime(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <textarea
              id="notes"
              className="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
              placeholder="Add any notes about this call..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <Button variant="ghost" onClick={handleSkip}>
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedOutcome || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Outcome'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
